// Barcode-first capture surface. Opens the device camera and tries to
// read a 1D barcode (UPC-A, EAN-13, EAN-8, Code-128, Code-39, ITF) in
// real time. NDC barcodes on US prescription bottles are typically
// Code-128 or UPC-A; OTC retail products use UPC-A / EAN-13.
//
// Why barcode-first: a successful scan is 100% accurate (it's a
// checksummed identifier, not interpretation). When it works, the user
// never has to take a photo at all, which is the single biggest senior
// UX win we can ship. When it doesn't (bad lighting, no barcode
// visible, dietary-supplement-only product), we hand off to the photo
// OCR flow without making the user start over.
//
// Privacy: all scanning is client-side; only the resolved digits leave
// the browser, and they're never PHI.

import { BlockStack } from '@shopify/polaris';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType, type Result } from '@zxing/library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBarcodeResolve } from '../api/barcode';
import type { OcrCandidate, OcrDosingHint } from '../api/ocr';
import { AppPage } from '../layouts/AppPage';
import { CandidatePicker } from './CandidatePicker';
import { SeniorBanner } from './senior/SeniorBanner';
import { SeniorButton } from './senior/SeniorButton';
import { SeniorCard } from './senior/SeniorCard';

type State =
  | { kind: 'idle' }
  | { kind: 'scanning' }
  | { kind: 'resolving'; code: string }
  | { kind: 'candidates'; candidates: OcrCandidate[]; dosingHint: OcrDosingHint | null }
  | { kind: 'not-found'; code: string }
  | { kind: 'error'; message: string };

const HINTS = new Map<DecodeHintType, unknown>([
  [
    DecodeHintType.POSSIBLE_FORMATS,
    [
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.DATA_MATRIX,
    ],
  ],
  [DecodeHintType.TRY_HARDER, true],
]);

export function BarcodeCapture() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const resolve = useBarcodeResolve();
  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    // Clean up the camera stream on unmount. Returning the function
    // itself (rather than calling it) so the effect's cleanup phase
    // runs stopCamera when the component is torn down.
    return () => {
      const c = controlsRef.current;
      if (c) {
        try {
          c.stop();
        } catch {
          // ignore
        }
        controlsRef.current = null;
      }
    };
  }, []);

  function stopCamera() {
    const c = controlsRef.current;
    if (c) {
      try {
        c.stop();
      } catch {
        // ignore
      }
      controlsRef.current = null;
    }
  }

  async function start() {
    setState({ kind: 'scanning' });
    const reader = new BrowserMultiFormatReader(HINTS, { delayBetweenScanAttempts: 250 });
    try {
      // decodeFromVideoDevice picks the rear camera (deviceId=undefined)
      // when available and starts scanning automatically.
      const controls = await reader.decodeFromVideoDevice(
        undefined,
        videoRef.current ?? undefined,
        async (result: Result | undefined, err) => {
          if (result) {
            const code = result.getText();
            stopCamera();
            setState({ kind: 'resolving', code });
            try {
              const r = await resolve(code);
              if (r.resolved && r.candidates.length > 0) {
                setState({
                  kind: 'candidates',
                  candidates: r.candidates,
                  dosingHint: r.dosingHint ?? null,
                });
              } else {
                setState({ kind: 'not-found', code });
              }
            } catch (e) {
              setState({
                kind: 'error',
                message: e instanceof Error ? e.message : String(e),
              });
            }
          } else if (err && err.name !== 'NotFoundException') {
            // NotFoundException fires every frame the scanner doesn't
            // see a barcode; we ignore those. Anything else is a real
            // problem (camera lost, decode crash).
            // Don't tear down on a transient decode crash; just log.
            // eslint-disable-next-line no-console
            console.debug('barcode decode error', err.message);
          }
        },
      );
      controlsRef.current = controls;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({
        kind: 'error',
        message:
          /permission/i.test(msg) || /allow/i.test(msg)
            ? 'Please allow camera access, then try again.'
            : msg,
      });
    }
  }

  if (state.kind === 'candidates') {
    // A barcode hit is the highest-confidence path we have (it's a
    // checksummed identifier). Pass 'high' so the picker drops the
    // "pick the closest match" framing and goes straight to confirm.
    return (
      <CandidatePicker
        candidates={state.candidates}
        dosingHint={state.dosingHint}
        confidence="high"
        onCancel={() => {
          setState({ kind: 'idle' });
        }}
        onSelected={() => navigate('/medications')}
      />
    );
  }

  return (
    <AppPage
      title="Scan the barcode on your bottle"
      back={{ to: '/medications', label: 'Medications' }}
    >
      <BlockStack gap="400">
        <SeniorCard
          title="Point the camera at the barcode"
          body="The barcode is the thick black line pattern on the side or bottom of the bottle. Hold the camera steady; we'll beep as soon as we read it."
        >
          {state.kind === 'error' && (
            <SeniorBanner title="Camera trouble" tone="critical">
              {state.message}
            </SeniorBanner>
          )}
          {state.kind === 'not-found' && (
            <SeniorBanner title="We could not find that barcode" tone="warning">
              <p style={{ margin: '0 0 12px' }}>
                The barcode read fine ({state.code}), but we did not find a matching medicine or
                product. Try a photo of the label, or type the name in.
              </p>
            </SeniorBanner>
          )}

          {(state.kind === 'scanning' || state.kind === 'resolving') && (
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                background: '#000',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <video
                ref={videoRef}
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Senior-friendly target frame so they know where to aim. */}
              <div
                style={{
                  position: 'absolute',
                  inset: '20% 10%',
                  border: '3px solid rgba(255,255,255,0.85)',
                  borderRadius: 12,
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 12,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: '#fff',
                  fontSize: 16,
                  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                }}
              >
                {state.kind === 'resolving'
                  ? `Looking up ${state.code}...`
                  : 'Hold steady, aim the barcode inside the box'}
              </div>
            </div>
          )}

          {state.kind === 'idle' || state.kind === 'error' || state.kind === 'not-found' ? (
            <SeniorButton onClick={() => void start()} variant="primary" fullWidth>
              {state.kind === 'idle' ? 'Open camera' : 'Try again'}
            </SeniorButton>
          ) : (
            <SeniorButton
              onClick={() => {
                stopCamera();
                setState({ kind: 'idle' });
              }}
              fullWidth
            >
              Stop scanning
            </SeniorButton>
          )}

          <SeniorButton
            onClick={() => navigate('/medications/add-by-photo')}
            fullWidth
            data-testid="photo-instead"
          >
            Take a photo of the label instead
          </SeniorButton>
          <SeniorButton
            onClick={() => navigate('/medications/add-by-name')}
            fullWidth
            data-testid="type-instead"
          >
            Type the name instead
          </SeniorButton>
        </SeniorCard>
      </BlockStack>
    </AppPage>
  );
}
